import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PropertyCharacteristicsDto, PropertyPhotoDto, UserApplications } from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';

import { EtxeEnumsNiceLabelUtils } from '../../../../../utils/etxe-enums-nice-label.utils';

@Component({
  selector: 'etxe-photo-manage',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatTooltipModule,
    DragDropModule,
  ],
  templateUrl: './photo-manage.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoManageComponent {
  private appStore = inject(ApplicationStore);
  private userStore = inject(UserStore);
  private alertSnackbar = inject(AlertSnackBarService);

  photos = input.required<PropertyPhotoDto[]>();
  propertyCharacteristics = input.required<PropertyCharacteristicsDto>();

  readonly isAdmin = computed(
    () => this.appStore.name() === UserApplications.ADMIN && this.userStore.isAdmin(),
  );

  addClicked = output<void>();
  doneClicked = output<void>();
  photosUpdated = output<PropertyPhotoDto[]>();

  // Extract flat list of characteristics from the property DTO to populate dropdowns
  get availableCharacteristics() {
    const chars = this.propertyCharacteristics();
    const result = {
      general: [
        { label: 'Façade', value: 'general:facade:facade' },
        { label: 'Vue / Panorama', value: 'general:view:view' },
        { label: 'Vue aérienne / Drone', value: 'general:aerial_view:aerial_view' },
        { label: 'Environnement / Quartier', value: 'general:surroundings:surroundings' },
        { label: 'Extérieur', value: 'general:exterior_general:exterior_general' },
        { label: 'Autre', value: 'general:other:other' },
      ] as { label: string; value: string }[],
      interior: [] as { label: string; value: string }[],
      exterior: [] as { label: string; value: string }[],
      commonAreas: [] as { label: string; value: string }[],
      parkings: [] as { label: string; value: string }[],
    };

    if (!chars) return result;

    const translateExterior = (cat: string) => {
      const map: Record<string, string> = {
        balcony: 'Balcon',
        terrace: 'Terrasse',
        garden: 'Jardin',
        loggia: 'Loggia',
        swimmingPool: 'Piscine',
        cellar: 'Cave',
      };
      return map[cat] || cat;
    };

    // Interior
    if (chars.interior) {
      // 1. Rooms (Salon, Cuisine, Bureau, etc.)
      if (Array.isArray(chars.interior.rooms)) {
        chars.interior.rooms.forEach((room, idx) => {
          if (room.id) {
            const roomLabel = room.type ? EtxeEnumsNiceLabelUtils.room(room.type) : `Pièce ${idx + 1}`;
            const precision = room.precision ? ` (${room.precision})` : '';
            const surface = room.surface ? ` - ${room.surface} m²` : '';
            result.interior.push({
              label: `${roomLabel}${precision}${surface}`,
              value: `interior:${room.type || 'rooms'}:${room.id}`,
            });
          }
        });
      }

      // 2. Bedrooms
      if (Array.isArray(chars.interior.bedroom)) {
        chars.interior.bedroom.forEach((bed, idx) => {
          if (bed.id) {
            const precision = bed.precision ? ` (${bed.precision})` : '';
            const surface = bed.surface ? ` - ${bed.surface} m²` : '';
            result.interior.push({
              label: `Chambre ${idx + 1}${precision}${surface}`,
              value: `interior:bedroom:${bed.id}`,
            });
          }
        });
      }

      // 3. Bathrooms
      if (Array.isArray(chars.interior.bathroom)) {
        chars.interior.bathroom.forEach((bath, idx) => {
          if (bath.id) {
            const precision = bath.precision ? ` (${bath.precision})` : '';
            const surface = bath.surface ? ` - ${bath.surface} m²` : '';
            result.interior.push({
              label: `Salle de bain ${idx + 1}${precision}${surface}`,
              value: `interior:bathroom:${bath.id}`,
            });
          }
        });
      }

      // 4. Shower rooms
      if (Array.isArray(chars.interior.showerRoom)) {
        chars.interior.showerRoom.forEach((shower, idx) => {
          if (shower.id) {
            const precision = shower.precision ? ` (${shower.precision})` : '';
            const surface = shower.surface ? ` - ${shower.surface} m²` : '';
            result.interior.push({
              label: `Salle d'eau ${idx + 1}${precision}${surface}`,
              value: `interior:showerRoom:${shower.id}`,
            });
          }
        });
      }
    }

    // Exterior
    if (chars.exterior) {
      Object.entries(chars.exterior).forEach(([cat, list]) => {
        if (Array.isArray(list)) {
          list.forEach((item, idx) => {
            if (item.id) {
              const catLabel = translateExterior(cat);
              const precision = item.precision ? ` (${item.precision})` : '';
              const surface = item.surface ? ` - ${item.surface} m²` : '';
              result.exterior.push({
                label: `${catLabel} ${idx + 1}${precision}${surface}`,
                value: `exterior:${cat}:${item.id}`,
              });
            }
          });
        }
      });
    }

    // Common Areas
    if (chars.commonAreas) {
      chars.commonAreas.forEach((item, idx) => {
        if (item.id) {
          const typeLabel = item.type
            ? EtxeEnumsNiceLabelUtils.commonArea(item.type)
            : `Espace commun ${idx + 1}`;
          const precision = item.precision ? ` (${item.precision})` : '';
          result.commonAreas.push({
            label: `${typeLabel}${precision}`,
            value: `commonAreas:${item.type || 'commonAreas'}:${item.id}`,
          });
        }
      });
    }

    // Parkings
    if (chars.parkings) {
      chars.parkings.forEach((item, idx) => {
        if (item.id) {
          const typeLabel = item.type ? EtxeEnumsNiceLabelUtils.parking(item.type) : `Parking ${idx + 1}`;
          const count = item.count > 1 ? ` (x${item.count})` : '';
          result.parkings.push({
            label: `${typeLabel}${count}`,
            value: `parkings:${item.type || 'parkings'}:${item.id}`,
          });
        }
      });
    }

    return result;
  }

  getRefValue(photo: PropertyPhotoDto): string {
    if (!photo.characteristicRef) return '';
    const ref = photo.characteristicRef;
    const allOptions = [
      ...this.availableCharacteristics.general,
      ...this.availableCharacteristics.interior,
      ...this.availableCharacteristics.exterior,
      ...this.availableCharacteristics.commonAreas,
      ...this.availableCharacteristics.parkings,
    ];

    const exact = allOptions.find((opt) => opt.value === `${ref.section}:${ref.category}:${ref.id}`);
    if (exact) return exact.value;

    const matchById = allOptions.find((opt) => opt.value.endsWith(`:${ref.id}`));
    if (matchById) return matchById.value;

    return `${ref.section}:${ref.category}:${ref.id}`;
  }

  onCharacteristicChange(photo: PropertyPhotoDto, value: string) {
    const updatedPhotos = [...this.photos()];
    const index = updatedPhotos.findIndex((p) => p === photo);
    if (index === -1) return;

    if (value === '') {
      updatedPhotos[index] = { ...photo, characteristicRef: undefined };
    } else {
      const [section, category, id] = value.split(':');
      if (index === 0 && section !== 'interior') {
        this.alertSnackbar.warn({
          message:
            'La photo de couverture ne peut pas être une photo masquée avant la visite (extérieur, parties communes, etc.).',
        });
        return;
      }
      updatedPhotos[index] = {
        ...photo,
        characteristicRef: {
          section: section as unknown as NonNullable<PropertyPhotoDto['characteristicRef']>['section'],
          category: category as unknown as NonNullable<PropertyPhotoDto['characteristicRef']>['category'],
          id,
        },
      };
    }

    this.photosUpdated.emit(updatedPhotos);
  }

  removePhoto(photo: PropertyPhotoDto) {
    const updatedPhotos = this.photos().filter((p) => p !== photo);
    this.reindex(updatedPhotos);
    this.photosUpdated.emit(updatedPhotos);
  }

  moveUp(index: number) {
    if (index === 0) return;
    const updatedPhotos = [...this.photos()];
    if (index === 1 && this.isMandateOnly(updatedPhotos[1])) {
      this.alertSnackbar.warn({
        message:
          'Cette photo est visible uniquement après la première visite et ne peut pas être définie en photo de couverture.',
      });
      return;
    }
    const temp = updatedPhotos[index];
    updatedPhotos[index] = updatedPhotos[index - 1];
    updatedPhotos[index - 1] = temp;
    this.reindex(updatedPhotos);
    this.photosUpdated.emit(updatedPhotos);
  }

  handleDrop(event: CdkDragDrop<PropertyPhotoDto[]>) {
    const updatedPhotos = [...this.photos()];
    if (event.currentIndex === 0 && this.isMandateOnly(updatedPhotos[event.previousIndex])) {
      this.alertSnackbar.warn({
        message:
          'Une photo visible uniquement après la première visite ne peut pas être placée en photo de couverture.',
      });
      return;
    }
    moveItemInArray(updatedPhotos, event.previousIndex, event.currentIndex);
    this.reindex(updatedPhotos);
    this.photosUpdated.emit(updatedPhotos);
  }

  moveDown(index: number) {
    if (index === this.photos().length - 1) return;
    const updatedPhotos = [...this.photos()];
    if (index === 0 && this.isMandateOnly(updatedPhotos[1])) {
      this.alertSnackbar.warn({
        message:
          'La photo suivante est visible uniquement après la première visite et ne peut pas devenir la couverture.',
      });
      return;
    }
    const temp = updatedPhotos[index];
    updatedPhotos[index] = updatedPhotos[index + 1];
    updatedPhotos[index + 1] = temp;
    this.reindex(updatedPhotos);
    this.photosUpdated.emit(updatedPhotos);
  }

  toggleHideBeforeMandate(photo: PropertyPhotoDto) {
    const updatedPhotos = [...this.photos()];
    const index = updatedPhotos.findIndex((p) => p === photo);
    if (index !== -1) {
      if (index === 0 && !photo.isHiddenBeforeMandate) {
        this.alertSnackbar.warn({
          message: 'La photo de couverture doit rester visible par tous.',
        });
        return;
      }
      updatedPhotos[index] = {
        ...photo,
        isHiddenBeforeMandate: !photo.isHiddenBeforeMandate,
      };
      this.photosUpdated.emit(updatedPhotos);
    }
  }

  isMandateOnly(photo: PropertyPhotoDto): boolean {
    if (photo.isHiddenBeforeMandate) return true;
    const section = photo.characteristicRef?.section;
    return !!section && section !== 'interior';
  }

  private reindex(photos: PropertyPhotoDto[]) {
    photos.forEach((photo, idx) => {
      photo.order = idx;
    });
  }
}
